import { ReactElement, useEffect, useState, useRef } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import Modal from 'react-modal';

import { useOrcamentoForm } from '../../../contexts/OrcamentoFormProvider';
import { useLoadingStore } from '../../../stores/useLoadingStore';
import { useProdutosOrcamentoStore } from '../../../stores/useProdutosOrcamento';
import { Button } from '../../Button';
import { Input } from '../../Input';

Modal.setAppElement('#root');

export function PlanosForm(): ReactElement {
  const { register, handleSubmit, onSubmit, errors, watch, setValue, control } =
    useOrcamentoForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'planos',
  });
  const timeoutRefs = useRef<Record<number, NodeJS.Timeout | null>>({});
  const planos = watch('planos') || [];
  const [totalFinal, setTotalFinal] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const { loading } = useLoadingStore();

  useEffect(() => {
    calculaTotal();
  }, [JSON.stringify(planos)]);

  useEffect(() => {
    if (!fields || (fields.length == 0 && !fields[0]?.id)) {
      handleAddRow();
    }
  }, []);

  const produtos = useProdutosOrcamentoStore(x => x.produtos);

  const handleAddRow = () => {
    append({
      id: '',
      qtdTitular: '',
      qtdDependente: '',
    });
  };

  const handleRemoveRow = index => {
    remove(index);
  };

  const calculaTotal = () => {
    if (planos.length > 0) {
      planos.forEach((plano, index) => {
        // Busca o produto selecionado
        const produto = produtos.find(p => p.id_prodcomerc == plano.id);
        if (!produto) return;
        const valorUnitario =
          parseFloat(produto.formasPagamentoEmpresa[0].vl_valor) || 0;

        // Converte os campos em número (se estiverem vazios, vira 0)
        const qtdTitular = Number(plano.qtdTitular) || 0;
        const qtdDependente = Number(plano.qtdDependente) || 0;

        // Soma total de vidas
        const qtdTotal = qtdTitular + qtdDependente;

        // Calcula valor total (sempre baseado no valor atual)
        const total = qtdTotal * valorUnitario;

        // Atualiza no formulário (sem somar o anterior)
        setValue(`planos.${index}.total`, total.toFixed(2));
      });
      const totalFinalCalculado = planos.reduce((acc, plano) => {
        return acc + (parseFloat(plano.total) || 0);
      }, 0);
      setTotalFinal(totalFinalCalculado);
    }
  };

  const handleGenerateOrcamento = () => {
    if (planos.length === 0) {
      alert('Adicione pelo menos um plano para gerar o orçamento.');
      return;
    } else {
      const Erros = [];
      const qtdTotal = planos.reduce((total, plano) => {
        return (
          total +
          Number(plano.qtdTitular || 0) +
          Number(plano.qtdDependente || 0)
        );
      }, 0);
      if (qtdTotal < 2) {
        alert('A proposta deve ter  pelo menos 2 vidas');
      }
      for (let i = 0; i < planos.length; i++) {
        const plano = planos[i];
        if (
          !plano.id ||
          ((!plano.qtdTitular || Number(plano.qtdTitular) < 0) &&
            (!plano.qtdDependente || Number(plano.qtdDependente) < 0))
        ) {
          Erros.push({
            message:
              'Preencha todos os campos dos planos antes de gerar o orçamento.',
          });
          return;
        }
      }
      if (Erros.length > 0) {
        alert(Erros.map(err => err.message).join('\n'));
        return;
      }
    }
    handleSubmit(onSubmit)();
  };

  return (
    <div className="p-4">
      {planos.length > 0 && (
        <>
          <h2 className="text-xl font-medium text-primary-main mb-4">
            Tabela de Produtos
          </h2>

          <table className="w-full table-fixed border border-gray-300 rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 w-1/5 text-left">Nome</th>
                <th className="p-2 w-1/5 text-left">Titulares</th>
                <th className="p-2 w-1/5 text-left">Dependentes</th>
                <th className="p-2 w-1/5 text-left">Total por produto</th>
                <th className="p-2 w-1/5 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id} className="border-t">
                  <td className="p-2 w-1/5">
                    <Controller
                      control={control}
                      name={`planos.${index}.id`}
                      render={({ field: controllerField }) => {
                        const filteredOptions = produtos.filter(opt => {
                          const jaSelecionado = planos.some(
                            (p, i) => p.id == opt.Id,
                          );
                          return (
                            !jaSelecionado || opt.Id == controllerField.value
                          );
                        });

                        const selectedOption = filteredOptions.find(
                          opt => opt.Id == controllerField.value,
                        );

                        return (
                          <>
                            {/* Botão que abre o modal */}
                            {selectedOption ? (
                              <button
                                type="button"
                                className={`
                                w-full text-left
                                p-0
                                text-sm font-medium
                                text-black
                                hover:text-blue-800
                                focus:outline-none
                              `}
                                onClick={() => setIsOpen(true)}
                              >
                                {selectedOption.Nome}
                              </button>
                            ) : (
                              <div className="p-4">
                                <Button
                                  type="button"
                                  onClick={() => setIsOpen(true)}
                                  text="Selecionar Produto"
                                />
                              </div>
                            )}

                            {/* Modal com lista de opções */}
                            <Modal
                              isOpen={isOpen}
                              onRequestClose={() => setIsOpen(false)}
                              className="bg-white p-6 rounded-lg shadow-xl w-[400px] mx-auto mt-20"
                              overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center"
                            >
                              <h2 className="text-lg font-semibold mb-4">
                                Selecione um plano
                              </h2>
                              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {filteredOptions.map(opt => (
                                  <button
                                    key={opt.Id}
                                    className={`w-full text-left p-3 rounded-md hover:bg-blue-100 ${
                                      opt.Id === controllerField.value
                                        ? 'bg-blue-50'
                                        : ''
                                    }`}
                                    onClick={() => {
                                      controllerField.onChange(opt.Id);
                                      setValue(
                                        `planos.${index}.id`,
                                        String(opt.Id),
                                      );
                                      setIsOpen(false);
                                    }}
                                  >
                                    {opt.Nome}
                                  </button>
                                ))}
                              </div>
                              <button
                                className="mt-4 text-sm text-gray-600 hover:text-gray-800"
                                onClick={() => setIsOpen(false)}
                              >
                                Cancelar
                              </button>
                            </Modal>
                          </>
                        );
                      }}
                    />
                  </td>
                  <td className="p-2 w-1/5">
                    <Input
                      label=""
                      type="number"
                      min={0}
                      onKeyDown={e => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                      placeholder="QTD de titulares do plano"
                      errorMessage={errors.planos?.[index]?.qtdTitular?.message}
                      {...register(`planos.${index}.qtdTitular`)}
                    />
                  </td>
                  <td className="p-2 w-1/5">
                    <Input
                      label=""
                      type="number"
                      min={0}
                      onKeyDown={e => {
                        if (e.key === '-' || e.key === 'e') e.preventDefault();
                      }}
                      placeholder="Qtd de dependentes do plano"
                      errorMessage={
                        errors.planos?.[index]?.qtdDependente?.message
                      }
                      {...register(`planos.${index}.qtdDependente`)}
                    />
                  </td>
                  <td className="p-2 w-1/5">
                    <Input
                      disabled
                      label=""
                      type="number"
                      placeholder="Valor total do plano"
                      {...register(`planos.${index}.total`)}
                    />
                  </td>
                  <td className="p-2 pl-24 w-1/5 text-center">
                    <Button
                      type="button"
                      onClick={() => handleRemoveRow(index)}
                      style={{ background: '#CC0505' }}
                      text="Remover"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <table className="w-full border border-gray-300 justify-left rounded-lg mt-4">
        <thead className="bg-gray-100">
          <tr className="border-t flex flex-row-reverse">
            {planos.length > 0 && (
              <th className="p-2 w-1/5 text-left">Total da Proposta</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t flex flex-row-reverse">
            {planos.length > 0 && (
              <td className="p-2 w-1/5">
                <Input
                  disabled
                  label=""
                  placeholder="Valor total do plano"
                  value={`R$ ${totalFinal.toFixed(2).replace('.', ',')}`}
                />
              </td>
            )}
            <td className="p-2 pl-24 w-1/5 text-center"></td>
            <td className="p-2 pl-24 w-1/5 text-center"></td>
            <td className="p-2 pl-24 w-1/5 text-center"></td>
            <td className="flex p-4">
              <Button
                type="button"
                text="Adicionar Produto"
                style={{ width: '100%' }}
                onClick={handleAddRow}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="flex justify-end mt-4 gap-6">
        <Button
          disabled={loading}
          type="button"
          text={loading ? 'Enviando...' : 'Gerar Proposta Comercial'}
          onClick={handleGenerateOrcamento}
        />
      </div>
    </div>
  );
}

export default PlanosForm;
